import { StandardEvents } from '@shopify/events';
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/* New Horizon — minimal behavior extensions over native Horizon lifecycle/events. */
const qs = (root, selector) => {
  try { return root.querySelector(selector); } catch { return null; }
};
const qsa = (root, selector) => {
  try { return [...root.querySelectorAll(selector)]; } catch { return []; }
};
const parseJSON = (node, fallback = {}) => {
  try { return JSON.parse(node?.textContent || ''); } catch { return fallback; }
};
const setThemeHTML = (node, html) => {
  if (!node) return;
  const value = String(html ?? '');
  if (node.innerHTML !== value) node.innerHTML = value;
};

function formatMoney(cents, format = '${{amount}}') {
  const value = Number(cents || 0);
  const token = /\{\{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator|_with_apostrophe_separator)?)\s*\}\}/;
  const match = String(format || '').match(token);
  const kind = match?.[1] || 'amount';
  let decimals = 2;
  let decimal = '.';
  let thousands = ',';
  if (kind.includes('no_decimals')) decimals = 0;
  if (kind.includes('comma_separator')) { decimal = ','; thousands = '.'; }
  if (kind.includes('apostrophe_separator')) thousands = "'";
  const fixed = (value / 100).toFixed(decimals);
  let [whole, fraction] = fixed.split('.');
  whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  const amount = fraction === undefined ? whole : `${whole}${decimal}${fraction}`;
  return match ? String(format).replace(token, amount) : amount;
}

function initTopState() {
  let frame = 0;
  let previous = null;

  const update = () => {
    frame = 0;
    const atTop = getScrollTop() <= 1;
    if (atTop === previous) return;
    previous = atTop;
    document.documentElement.classList.toggle('nh-at-page-top', atTop);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };

  let scrollTarget;
  const bindScroll = () => {
    scrollTarget?.removeEventListener('scroll', schedule);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', schedule, { passive: true });
    update();
  };
  bindScroll();
  scrollContainerMediaQuery.addEventListener('change', bindScroll);
}

function findCartDialog() {
  return qs(document, '#cart-drawer dialog[ref="panel"], #cart-drawer .theme-drawer__dialog');
}
function cartDrawerIsOpen() {
  const dialog = findCartDialog();
  const drawer = dialog?.closest('theme-drawer') || qs(document, '#cart-drawer');
  return Boolean(dialog?.open || drawer?.hasAttribute('open'));
}
function cartConfig() {
  return parseJSON(qs(document, '[data-nh-cart-config]'), {});
}


const CART_RESERVATION_STORAGE_KEY = 'new-horizon:cart-reservation:v1';
let cartReservationTimer = 0;
let cartReservationMemoryState = null;

function cartReservationNode() {
  return qs(document, '[data-nh-cart-reservation]');
}

function cartReservationSettings(node = cartReservationNode()) {
  if (!node) return null;
  const enabled = node.dataset.enabled === 'true';
  const minutes = Math.min(10, Math.max(5, Number(node.dataset.durationMinutes || 10)));
  return {
    enabled,
    minutes,
    token: String(node.dataset.cartToken || ''),
    itemCount: Math.max(0, Number(node.dataset.cartItemCount || 0)),
  };
}

function readCartReservationState() {
  if (cartReservationMemoryState) return cartReservationMemoryState;
  try {
    const value = localStorage.getItem(CART_RESERVATION_STORAGE_KEY);
    if (value) {
      const state = JSON.parse(value);
      if (state && Number.isFinite(Number(state.expiresAt))) {
        cartReservationMemoryState = state;
        return state;
      }
    }
  } catch { /* Fall back to memory-only state below. */ }
  return null;
}

function writeCartReservationState(state) {
  cartReservationMemoryState = state;
  try { localStorage.setItem(CART_RESERVATION_STORAGE_KEY, JSON.stringify(state)); }
  catch { /* Memory fallback keeps the timer stable for this page session. */ }
}

function clearCartReservationState() {
  cartReservationMemoryState = null;
  try { localStorage.removeItem(CART_RESERVATION_STORAGE_KEY); }
  catch { /* No-op. */ }
}

function ensureCartReservationState(node = cartReservationNode()) {
  const config = cartReservationSettings(node);
  if (!config) return null;
  if (!config.enabled || config.itemCount <= 0) {
    clearCartReservationState();
    return null;
  }

  const current = readCartReservationState();
  const tokenMatches = current?.token === config.token;
  const durationMatches = Number(current?.minutes) === config.minutes;
  if (tokenMatches && durationMatches && Number.isFinite(Number(current?.expiresAt))) return current;

  const next = {
    token: config.token,
    minutes: config.minutes,
    expiresAt: Date.now() + config.minutes * 60_000,
  };
  writeCartReservationState(next);
  return next;
}

function renderCartReservation() {
  const node = cartReservationNode();
  const config = cartReservationSettings(node);
  if (!node || !config) return false;

  if (!config.enabled || config.itemCount <= 0) {
    node.hidden = true;
    clearCartReservationState();
    return false;
  }

  const state = ensureCartReservationState(node);
  if (!state) {
    node.hidden = true;
    return false;
  }

  node.hidden = false;
  const remainingMs = Math.max(0, Number(state.expiresAt) - Date.now());
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const time = qs(node, '[data-nh-cart-reservation-time]');
  if (time) {
    const nextText = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    if (time.textContent !== nextText) time.textContent = nextText;
    time.setAttribute('datetime', `PT${minutes}M${seconds}S`);
  }
  return remainingMs > 0;
}

function stopCartReservationTimer() {
  if (!cartReservationTimer) return;
  clearInterval(cartReservationTimer);
  cartReservationTimer = 0;
}

function startCartReservationTimer() {
  stopCartReservationTimer();
  if (!renderCartReservation()) return;
  cartReservationTimer = setInterval(() => {
    if (!renderCartReservation()) stopCartReservationTimer();
  }, 1000);
}

function scheduleCartReservationStart(attempt = 0) {
  requestAnimationFrame(() => {
    if (!cartDrawerIsOpen()) return;
    const config = cartReservationSettings();
    if ((!config || config.itemCount <= 0) && attempt < 3) {
      scheduleCartReservationStart(attempt + 1);
      return;
    }
    startCartReservationTimer();
  });
}

function initCartReservation() {
  renderCartReservation();

  document.addEventListener('theme-drawer:close', event => {
    const drawer = event.target instanceof Element ? event.target.closest('theme-drawer') : null;
    if (drawer?.id === 'cart-drawer') stopCartReservationTimer();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopCartReservationTimer();
      return;
    }
    if (cartDrawerIsOpen()) scheduleCartReservationStart();
  });

  if (cartDrawerIsOpen()) scheduleCartReservationStart();
}
function findCartPanel(dialog) {
  return qs(dialog, '.cart-drawer__inner') || qs(dialog, 'cart-drawer-component') || dialog;
}
function checkoutButton(panel) {
  return qs(panel, 'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], [class*="checkout-button"]');
}
function syncCartBubbleTheme(panel) {
  const sourceBubble = qs(document, '.header-actions__cart-icon .cart-bubble');
  const sourceBackground = qs(sourceBubble, '.cart-bubble__background');
  const drawerBadge = qs(panel, '.theme-drawer__badge');
  if (!sourceBubble || !drawerBadge) return;

  const backgroundColor = sourceBackground ? getComputedStyle(sourceBackground).backgroundColor : '';
  const textColor = getComputedStyle(sourceBubble).color;
  if (backgroundColor) drawerBadge.style.setProperty('--nh-cart-bubble-background', backgroundColor);
  else drawerBadge.style.removeProperty('--nh-cart-bubble-background');
  if (textColor) drawerBadge.style.setProperty('--nh-cart-bubble-text', textColor);
  else drawerBadge.style.removeProperty('--nh-cart-bubble-text');
}
let cartEnhanceQueued = false;
function scheduleCartEnhance() {
  if (cartEnhanceQueued) return;
  cartEnhanceQueued = true;
  queueMicrotask(() => {
    cartEnhanceQueued = false;
    if (cartDrawerIsOpen()) enhanceCart(findCartDialog());
  });
}
function updateFreeShipping(panel, config) {
  const bars = qsa(panel, '[data-nh-free-shipping]');
  qsa(panel, '.nh-free-shipping:not([data-nh-free-shipping])').forEach(node => node.remove());
  const bar = bars.shift();
  bars.forEach(node => node.remove());
  if (!bar) return;

  if (!config.freeShippingEnabled) {
    bar.hidden = true;
    return;
  }

  // These values belong to the same Shopify-rendered cart as the visible lines.
  // A second cart.js request can race that render and must not reset its progress.
  const itemCount = Number(bar.dataset.cartItemCount);
  const shippableTotal = Number(bar.dataset.shippingTotal);
  if (!Number.isFinite(itemCount) || !Number.isFinite(shippableTotal)) return;
  if (itemCount <= 0) {
    bar.hidden = true;
    return;
  }

  const currencyRate = Math.max(0, Number(window.Shopify?.currency?.rate || 1)) || 1;
  const threshold = Math.max(0, Math.round(Number(config.freeShippingThreshold || 0) * currencyRate * 100));
  const remaining = Math.max(0, threshold - shippableTotal);
  const ratio = threshold <= 0 ? 1 : Math.min(1, Math.max(0, shippableTotal) / threshold);
  const amount = formatMoney(remaining, config.moneyFormat);
  const template = remaining > 0
    ? (config.freeShippingRemainingMessage || 'You are {amount} away from free shipping')
    : (config.freeShippingReachedMessage || 'You have unlocked free shipping');
  const messageNode = qs(bar, '.nh-free-shipping__message');
  const message = String(template).replaceAll('{amount}', amount);
  setThemeHTML(messageNode, message);

  bar.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
  bar.setAttribute('aria-label', messageNode?.textContent || '');
  const freeShippingComplete = ratio >= 1;
  bar.toggleAttribute('data-complete', freeShippingComplete);
  bar.style.setProperty(
    '--nh-free-bar',
    freeShippingComplete
      ? (config.freeShippingReachedBarColor || config.freeShippingBarColor || '#000')
      : (config.freeShippingBarColor || '#000')
  );
  bar.style.setProperty('--nh-free-track', config.freeShippingTrackColor || '#e6e6e6');
  // Restore the confirmed value before revealing newly rendered markup. Reopening
  // or morphing the drawer must never replay a zero-to-total animation.
  bar.style.setProperty('--nh-free-progress-ratio', String(ratio));
  bar.hidden = false;
}
function updateCartFooterTotals(panel, config) {
  const bar = qs(panel, '[data-nh-free-shipping]');
  const subtotal = qs(panel, '[data-nh-cart-subtotal]');
  const discountRow = qs(panel, '[data-nh-cart-real-discount-row]');
  const discountValue = qs(panel, '[data-nh-cart-real-discount-value]');
  const deliveryRow = qs(panel, '[data-nh-cart-delivery-row]');
  const deliveryValue = qs(panel, '[data-nh-cart-delivery-value]');
  const estimatedTotal = qs(panel, '[data-nh-cart-estimated-total]');
  if (!bar || !subtotal || !deliveryRow || !deliveryValue || !estimatedTotal) return;

  const subtotalCents = Number(subtotal.dataset.cartSubtotalCents);
  const merchandiseTotalCents = Number(estimatedTotal.dataset.cartMerchandiseTotalCents);
  const discountCents = Number(discountValue?.dataset.cartDiscountCents || 0);
  const shippableTotal = Number(bar.dataset.shippingTotal);
  const requiresShipping = bar.dataset.requiresShipping === 'true';
  if (!Number.isFinite(subtotalCents) || !Number.isFinite(merchandiseTotalCents) || !Number.isFinite(shippableTotal)) return;

  if (discountRow) discountRow.hidden = !(Number.isFinite(discountCents) && discountCents > 0);

  deliveryRow.hidden = !requiresShipping;

  const currencyRate = Math.max(0, Number(window.Shopify?.currency?.rate || 1)) || 1;
  const threshold = Math.max(0, Math.round(Number(config.freeShippingThreshold || 0) * currencyRate * 100));
  const standardDeliveryRate = Number(String(config.standardDeliveryRate ?? 0).replace(',', '.'));
  const standardDelivery = Math.max(0, Math.round((Number.isFinite(standardDeliveryRate) ? standardDeliveryRate : 0) * currencyRate * 100));
  const qualifiesForFreeDelivery = requiresShipping && threshold > 0 && shippableTotal >= threshold;
  const deliveryCents = requiresShipping && !qualifiesForFreeDelivery ? standardDelivery : 0;
  const totalCents = merchandiseTotalCents + deliveryCents;

  if (requiresShipping) {
    deliveryValue.textContent = deliveryCents === 0
      ? (config.freeDeliveryLabel || 'FREE')
      : formatMoney(deliveryCents, config.moneyFormat);
    deliveryValue.dataset.deliveryCents = String(deliveryCents);
  }

  const formattedTotal = formatMoney(totalCents, config.moneyFormat);
  estimatedTotal.textContent = formattedTotal;
  estimatedTotal.setAttribute('value', formattedTotal);
}

function syncCartActionVisibility(panel, config) {
  const note = qs(panel, '[data-nh-cart-note-control]');
  const discount = qs(panel, '[data-nh-cart-discount-control]');
  const divider = qs(panel, '[data-nh-cart-actions-divider]');

  if (note) {
    note.hidden = config.showCartNote === false;
    const label = qs(note, '.cart-note__label');
    const nextLabel = config.cartNoteLabel || config.cartNoteDefaultLabel || '';
    if (label && nextLabel) setThemeHTML(label, nextLabel);
  }

  if (discount) {
    discount.hidden = config.showDiscount === false;
    const label = qs(discount, '.cart-discount__label');
    const nextLabel = config.discountLabel || config.discountDefaultLabel || '';
    if (label && nextLabel) setThemeHTML(label, nextLabel);
  }

  if (divider) divider.hidden = config.showCartNote === false || config.showDiscount === false;
  const actions = qs(panel, '.cart-actions');
  if (actions) actions.hidden = config.showCartNote === false && config.showDiscount === false;
}
function arrangeCartRegions(panel, config) {
  const scrollRegion = qs(panel, '[data-nh-cart-scroll-region]');
  const merchandisingSlot = qs(panel, '[data-nh-cart-merchandising-slot]');

  const originalTotalsNodes = qsa(panel, '.cart-totals__original-container');
  const originalTotals = originalTotalsNodes.shift();
  originalTotalsNodes.forEach((node) => node.remove());

  const actionNodes = qsa(panel, '.cart-actions');
  const actions = actionNodes.shift();
  actionNodes.forEach((node) => node.remove());

  if (scrollRegion && originalTotals && originalTotals.parentElement !== scrollRegion) {
    if (merchandisingSlot) scrollRegion.insertBefore(originalTotals, merchandisingSlot);
    else scrollRegion.append(originalTotals);
  }

  if (scrollRegion && actions && actions.parentElement !== scrollRegion) {
    if (merchandisingSlot) scrollRegion.insertBefore(actions, merchandisingSlot);
    else scrollRegion.append(actions);
  }

  const taxNote = qs(panel, '[data-nh-cart-tax-note]');
  if (taxNote) {
    if (!taxNote.dataset.nhNativeTaxHtml) taxNote.dataset.nhNativeTaxHtml = taxNote.innerHTML;
    const override = String(config.taxNoteOverride || '').trim();
    if (override) {
      setThemeHTML(taxNote, override);
    } else if (taxNote.dataset.nhNativeTaxHtml && taxNote.innerHTML !== taxNote.dataset.nhNativeTaxHtml) {
      taxNote.innerHTML = taxNote.dataset.nhNativeTaxHtml;
    }
  }

  return { scrollRegion, merchandisingSlot };
}

function syncCartPriceTheme() {
  const source = qs(document, '.new-horizon-product-price');
  if (!source) return;

  const current = qs(source, '.price:not(.compare-at-price), .price');
  const compare = qs(source, '.compare-at-price');
  const root = document.documentElement;

  if (current) {
    const style = getComputedStyle(current);
    root.style.setProperty('--nh-cart-current-price-color', style.color);
    root.style.setProperty('--nh-cart-current-price-weight', style.fontWeight);
    if (compare) {
      root.style.setProperty('--nh-cart-sale-price-color', style.color);
      root.style.setProperty('--nh-cart-sale-price-weight', style.fontWeight);
    } else {
      root.style.removeProperty('--nh-cart-sale-price-color');
      root.style.removeProperty('--nh-cart-sale-price-weight');
    }
  }

  if (compare) {
    const style = getComputedStyle(compare);
    root.style.setProperty('--nh-cart-compare-price-color', style.color);
    root.style.setProperty('--nh-cart-compare-price-weight', style.fontWeight);
  } else {
    root.style.removeProperty('--nh-cart-compare-price-color');
    root.style.removeProperty('--nh-cart-compare-price-weight');
  }
}

function cartProductIds(panel) {
  return new Set(
    qsa(panel, '.cart-items__table-row[data-product-id]')
      .map((row) => String(row.dataset.productId || '').trim())
      .filter(Boolean)
  );
}

let cartRecommendationQuickAddLoading = null;

function cartRecommendationQuickAddReady() {
  if (customElements.get('quick-add-component')) return true;
  if (!cartRecommendationQuickAddLoading) {
    // Cart recommendations are inserted after the initial lazy-component scan.
    // Prepare the existing module before exposing their Choose controls, so a
    // first click on a slow connection cannot be lost during element upgrade.
    cartRecommendationQuickAddLoading = import('@theme/quick-add')
      .then(() => scheduleCartEnhance())
      .catch(() => { cartRecommendationQuickAddLoading = null; });
  }
  return false;
}

function cartRecommendationsClone(template, panel) {
  const fresh = template?.content?.firstElementChild?.cloneNode(true);
  if (!fresh) return null;

  const inCart = cartProductIds(panel);
  qsa(fresh, '.nh-cart-recommendation[data-product-id]').forEach((card) => {
    if (inCart.has(String(card.dataset.productId || '').trim())) card.remove();
  });

  const visibleCards = qsa(fresh, '.nh-cart-recommendation[data-product-id]');
  if (!visibleCards.length) return null;

  const baseKey = fresh.dataset.nhRecommendationsKey || '';
  const visibleKey = visibleCards.map((card) => card.dataset.productId || '').join(',');
  fresh.dataset.nhRecommendationsKey = `${baseKey}|visible:${visibleKey}`;
  return fresh;
}

function applyCartRootConfig(config) {
  const root = document.documentElement;
  root.style.setProperty('--nh-cart-drawer-width', `${Number(config.drawerWidth || 500)}px`);
  root.style.setProperty('--nh-cart-mobile-inset', `${Number(config.mobileInset || 10)}px`);
  root.style.setProperty('--nh-cart-radius', `${Number(config.cornerRadius || 18)}px`);
  root.style.setProperty('--nh-cart-overlay-opacity', String(config.overlayOpacity ?? .5));

  const cartDrawerComponent = qs(document, '#cart-drawer cart-drawer-component');
  if (cartDrawerComponent) cartDrawerComponent.toggleAttribute('auto-open', config.autoOpen !== false);
}

function enhanceCart(dialog) {
  if (!dialog) return;
  const config = cartConfig();
  const panel = findCartPanel(dialog);

  // Restore progress before any computed-style reads can expose the server's
  // initially hidden bar in the newly morphed cart.
  updateFreeShipping(panel, config);
  updateCartFooterTotals(panel, config);

  // Computed-style reads are intentionally deferred until the drawer is used;
  // doing them during initial page load forced an avoidable layout flush.
  syncCartPriceTheme();
  syncCartBubbleTheme(panel);

  syncCartActionVisibility(panel, config);
  const { merchandisingSlot } = arrangeCartRegions(panel, config);

  const checkout = checkoutButton(panel);
  if (checkout && checkout.tagName !== 'INPUT') {
    const lock = qs(checkout, '.cart__checkout-lock');
    if (lock) lock.hidden = config.showCheckoutLock === false;
    const footer = checkout.closest('[class*="footer"], [class*="summary"], form') || checkout.parentElement;
    if (footer) {
      let terms = qs(footer, '.nh-cart-terms');
      if (config.showTerms) {
        if (!terms) {
          terms = document.createElement('label');
          terms.className = 'nh-cart-terms';
          terms.innerHTML = '<input type="checkbox" data-nh-cart-terms> <span></span>';
          checkout.before(terms);
        }

        const termsLabel = qs(terms, 'span');
        const termsInput = qs(terms, 'input');
        const nextTermsLabel = config.termsLabel || 'I agree to the terms and policies';
        if (termsLabel) {
          const currentUrl = qs(termsLabel, 'a')?.getAttribute('href') || '';
          const currentLabel = qs(termsLabel, 'a')?.innerHTML ?? termsLabel.innerHTML;
          if (currentLabel !== nextTermsLabel || currentUrl !== (config.termsUrl || '')) {
            termsLabel.replaceChildren();
            if (config.termsUrl) {
              const link = document.createElement('a');
              link.href = config.termsUrl;
              link.target = '_blank';
              link.rel = 'noopener';
              setThemeHTML(link, nextTermsLabel);
              termsLabel.append(link);
            } else setThemeHTML(termsLabel, nextTermsLabel);
          }
        }

        if (termsInput && !termsInput.dataset.nhBound) {
          termsInput.dataset.nhBound = 'true';
          termsInput.addEventListener('change', event => { checkout.disabled = !event.currentTarget.checked; });
        }
        checkout.disabled = !termsInput?.checked;
      } else {
        terms?.remove();
        checkout.disabled = false;
      }

      let payments = qs(footer, '.nh-cart-payment-icons');
      if (config.showPaymentIcons && !payments) {
        const template = qs(document, 'template[data-nh-cart-payment-icons]');
        if (template) {
          checkout.after(template.content.cloneNode(true));
          payments = qs(footer, '.nh-cart-payment-icons');
        }
      } else if (!config.showPaymentIcons) {
        payments?.remove();
        payments = null;
      }
      if (payments) {
        const singleLine = config.paymentIconsSingleLine === true;
        payments.classList.toggle('nh-cart-payment-icons--single-line', singleLine);
        payments.classList.toggle('payment-icons--single-line', singleLine);
      }

      let trust = qs(footer, '.nh-cart-trust');
      if (config.showTrustMessage && config.trustMessage) {
        if (!trust) {
          trust = document.createElement('p');
          trust.className = 'nh-cart-trust';
          checkout.after(trust);
        }
        setThemeHTML(trust, config.trustMessage);
      } else {
        trust?.remove();
      }

      let trustImage = qs(footer, '.nh-cart-trust-badge');
      if (config.showTrustImage) {
        if (!trustImage) {
          const template = qs(document, 'template[data-nh-cart-trust-image]');
          const clone = template?.content?.firstElementChild?.cloneNode(true);
          if (clone) {
            (trust || checkout).after(clone);
            trustImage = clone;
          }
        }
        if (trustImage) trustImage.style.setProperty('--nh-cart-trust-image-width', `${Number(config.trustImageWidth || 180)}px`);
      } else trustImage?.remove();
    }
  }

  let existingRecommendations = qs(panel, '.nh-cart-recommendations');
  if (config.showRecommendations) {
    import('@theme/product-form').catch(() => undefined);
  }
  if (!config.showRecommendations) {
    qsa(panel, '.nh-cart-recommendations').forEach(node => node.remove());
  } else {
    const tpl = qs(document, 'template[data-nh-cart-recommendations]');
    const fresh = cartRecommendationsClone(tpl, panel);
    if (fresh?.querySelector('quick-add-component') && !cartRecommendationQuickAddReady()) return;

    if (!fresh) {
      existingRecommendations?.remove();
      existingRecommendations = null;
    } else if (merchandisingSlot) {
      const currentKey = existingRecommendations?.dataset?.nhRecommendationsKey || '';
      const freshKey = fresh.dataset?.nhRecommendationsKey || '';

      if (!existingRecommendations) {
        merchandisingSlot.append(fresh);
        existingRecommendations = fresh;
      } else if (currentKey !== freshKey) {
        existingRecommendations.replaceWith(fresh);
        existingRecommendations = fresh;
      } else if (existingRecommendations.parentElement !== merchandisingSlot) {
        merchandisingSlot.append(existingRecommendations);
      }

      qsa(panel, '.nh-cart-recommendations').slice(1).forEach(node => node.remove());

      const heading = qs(existingRecommendations, '.nh-cart-recommendations__heading');
      if (heading && config.recommendationsHeading) {
        setThemeHTML(heading, config.recommendationsHeading);
      }

      const inline = config.recommendationsLayout === 'inline';
      existingRecommendations.classList.toggle('nh-cart-recommendations--inline', inline);
      existingRecommendations.classList.toggle('nh-cart-recommendations--stacked', !inline);
      if (config.recommendationsBackground) {
        existingRecommendations.style.setProperty('--nh-cart-recommendations-background', config.recommendationsBackground);
      } else {
        existingRecommendations.style.removeProperty('--nh-cart-recommendations-background');
      }
    }
  }
}
function initCart() {
  const config = cartConfig();
  applyCartRootConfig(config);
  initCartReservation();

  // The expensive cart composition (computed styles) is not needed at
  // page start. Prepare it only when the native Horizon drawer actually opens.
  document.addEventListener('theme-drawer:open', event => {
    const drawer = event.target instanceof Element ? event.target.closest('theme-drawer') : null;
    if (drawer?.id === 'cart-drawer') {
      scheduleCartEnhance();
      scheduleCartReservationStart();
    }
  });

  document.addEventListener('cart:rendered', event => {
    if (!(event.target instanceof Element) || !event.target.closest('#cart-drawer')) return;
    // CartItemsComponent emits this only after its native DOM lifecycle has run.
    scheduleCartEnhance();
    if (cartDrawerIsOpen()) scheduleCartReservationStart();
  });

  const afterCartMutation = event => {
    const pending = event?.promise;
    if (pending && typeof pending.then === 'function') {
      pending.then(result => {
        if (result?.detail?.didError || result?.userErrors?.length) {
          scheduleCartEnhance();
          return;
        }
        const quantity = Number(result?.cart?.totalQuantity ?? result?.detail?.itemCount ?? NaN);
        const successfulAdd = event?.action === 'add' && result?.cart && !result?.detail?.didError && !(result?.userErrors?.length);
        if (successfulAdd) {
          // A real add-to-cart action starts a fresh reservation window. Quantity
          // changes and removals use other Shopify actions and intentionally do not reset it.
          clearCartReservationState();
          stopCartReservationTimer();
        }
        if (Number.isFinite(quantity)) {
          if (quantity <= 0) {
            clearCartReservationState();
            stopCartReservationTimer();
          } else if (cartDrawerIsOpen()) {
            scheduleCartReservationStart();
          } else {
            requestAnimationFrame(() => renderCartReservation());
          }
        }
      }).catch(() => {
        scheduleCartEnhance();
      });
    }
  };

  [
    StandardEvents.cartLinesUpdate,
    StandardEvents.cartDiscountUpdate,
  ].forEach(name => document.addEventListener(name, afterCartMutation));

  // A restored drawer may already be open before this module registers listeners.
  if (cartDrawerIsOpen()) scheduleCartEnhance();
}


function initOfferCopy() {
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-nh-copy-code]');
    if (!button) return;
    const code = button.dataset.nhCopyCode || '';
    try { await navigator.clipboard.writeText(code); }
    catch {
      const input = document.createElement('textarea'); input.value = code; input.style.position='fixed'; input.style.opacity='0'; document.body.append(input); input.select(); document.execCommand('copy'); input.remove();
    }
    const label = qs(button,'[data-nh-copy-label]');
    if (label) { const old = label.textContent; label.textContent = button.dataset.copiedLabel || 'Copied'; setTimeout(() => label.textContent = button.dataset.copyLabel || old,1600); }
  });
}

let offerGroupingFrame = 0;

function isOfferSlot(node) {
  if (!(node instanceof Element)) return false;
  if (node.matches('.nh-offer[data-nh-offer]')) return true;
  if (!node.matches('shopify-block, .shopify-block, [id^="shopify-block-"]')) return false;
  return Boolean(qs(node, ':scope > .nh-offer[data-nh-offer]'));
}

function offerSlotFor(offer) {
  const parent = offer?.parentElement;
  if (parent && parent.matches('shopify-block, .shopify-block, [id^="shopify-block-"]') && qs(parent, ':scope > .nh-offer[data-nh-offer]') === offer) {
    return parent;
  }
  return offer;
}

function groupAdjacentOffers(normalize = false) {
  cancelAnimationFrame(offerGroupingFrame);
  offerGroupingFrame = requestAnimationFrame(() => {
    if (normalize) {
      qsa(document, '.nh-offer-cluster').forEach(cluster => {
        const parent = cluster.parentElement;
        if (!parent) return;
        while (cluster.firstChild) parent.insertBefore(cluster.firstChild, cluster);
        cluster.remove();
      });
    }

    const parents = new Set();
    qsa(document, '.nh-offer[data-nh-offer]').forEach(offer => {
      const slot = offerSlotFor(offer);
      const parent = slot?.parentElement;
      if (parent && !parent.closest('.nh-offer-cluster')) parents.add(parent);
    });

    parents.forEach(parent => {
      const children = [...parent.children];
      for (let index = 0; index < children.length;) {
        if (!isOfferSlot(children[index])) {
          index += 1;
          continue;
        }

        const run = [];
        let cursor = index;
        while (cursor < children.length && isOfferSlot(children[cursor])) {
          run.push(children[cursor]);
          cursor += 1;
        }

        if (run.length >= 2) {
          const cluster = document.createElement('div');
          cluster.className = 'nh-offer-cluster';
          parent.insertBefore(cluster, run[0]);
          run.forEach(node => cluster.append(node));
        }
        index = cursor;
      }
    });
  });
}

// Product badges use the same adjacent-block lifecycle as Offers.
let productBadgeGroupingFrame = 0;

function isProductBadgeSlot(node) {
  if (!(node instanceof Element)) return false;
  if (node.matches('.nh-product-badge-block[data-nh-product-badge]')) return true;
  if (!node.matches('shopify-block, .shopify-block, [id^="shopify-block-"]')) return false;
  return Boolean(qs(node, ':scope > .nh-product-badge-block[data-nh-product-badge]'));
}

function productBadgeSlotFor(badge) {
  const parent = badge?.parentElement;
  if (parent && parent.matches('shopify-block, .shopify-block, [id^="shopify-block-"]') && qs(parent, ':scope > .nh-product-badge-block[data-nh-product-badge]') === badge) {
    return parent;
  }
  return badge;
}

function groupAdjacentProductBadges(normalize = false) {
  cancelAnimationFrame(productBadgeGroupingFrame);
  productBadgeGroupingFrame = requestAnimationFrame(() => {
    if (normalize) {
      qsa(document, '.nh-product-badge-cluster').forEach(cluster => {
        const parent = cluster.parentElement;
        if (!parent) return;
        while (cluster.firstChild) parent.insertBefore(cluster.firstChild, cluster);
        cluster.remove();
      });
    }

    const parents = new Set();
    qsa(document, '.nh-product-badge-block[data-nh-product-badge]').forEach(badge => {
      const slot = productBadgeSlotFor(badge);
      const parent = slot?.parentElement;
      if (parent && !parent.closest('.nh-product-badge-cluster')) parents.add(parent);
    });

    parents.forEach(parent => {
      const children = [...parent.children];
      for (let index = 0; index < children.length;) {
        if (!isProductBadgeSlot(children[index])) {
          index += 1;
          continue;
        }

        const run = [];
        let cursor = index;
        while (cursor < children.length && isProductBadgeSlot(children[cursor])) {
          run.push(children[cursor]);
          cursor += 1;
        }

        if (run.length >= 2) {
          const cluster = document.createElement('div');
          const firstBadge = run[0].matches('[data-nh-product-badge]')
            ? run[0] : qs(run[0], '[data-nh-product-badge]');
          cluster.className = `nh-product-badge-cluster nh-product-badge-block--${firstBadge?.dataset.badgeAlignment || 'left'}`;
          cluster.style.setProperty('--nh-product-badge-gap', firstBadge?.style.getPropertyValue('--nh-product-badge-gap') || '6px');
          parent.insertBefore(cluster, run[0]);
          run.forEach(node => cluster.append(node));
        }
        index = cursor;
      }
    });
  });
}

initTopState();
initCart();
initOfferCopy();
groupAdjacentOffers();
groupAdjacentProductBadges();

document.addEventListener(StandardEvents.productSelect, event => {
  const refreshProductExtensions = () => requestAnimationFrame(() => {
    groupAdjacentOffers(true);
    groupAdjacentProductBadges(true);
    syncCartPriceTheme();
  });
  const pending = event?.promise;
  if (pending && typeof pending.then === 'function') pending.then(refreshProductExtensions).catch(() => undefined);
  else refreshProductExtensions();
});

document.addEventListener('shopify:section:load', event => {
  if (event.target?.querySelector?.('.new-horizon-product-price')) syncCartPriceTheme();
});

['shopify:block:load', 'shopify:block:select', 'shopify:block:deselect', 'shopify:section:load', 'shopify:section:reorder'].forEach(name => {
  document.addEventListener(name, () => {
    groupAdjacentOffers(true);
    groupAdjacentProductBadges(true);
  });
});
